import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { Settings } from '../types';
import { useNavigate } from 'react-router-dom';
import { showToast } from '../utils/toaster';

const SettingsPage: React.FC = () => {
  const { state, actions } = useApp();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<Settings>({
    badge_threshold: 5,
    metrics_retention: 360, // 6 hours in minutes
    task_activity_window: 60,
    connections_window: 60,
    latency_window: 60,
    onboarding_channel_ttl_seconds: 30,
    refresh_interval_seconds: 5, // Default 5 seconds
    log_timestamp_format: 'local', // Default local time
    bucket_seconds: 30, // Default 30 seconds
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Update form data when settings load
  useEffect(() => {
    if (state.settings) {
      setFormData({
        ...state.settings,
        // Only convert hours to minutes for typical hour values (6, 12, 24, 48, 72, 168)
        // Common hour values that would be set in the old system
        // This prevents converting legitimate minute values incorrectly
        metrics_retention: (state.settings.metrics_retention === 6 || state.settings.metrics_retention === 12 || 
                           state.settings.metrics_retention === 24 || state.settings.metrics_retention === 48 || 
                           state.settings.metrics_retention === 72 || state.settings.metrics_retention === 168)
          ? state.settings.metrics_retention * 60 
          : state.settings.metrics_retention
      });
    }
  }, [state.settings]);

  // Keyboard shortcut for save (Ctrl+S or Cmd+S)
  useEffect(() => {
    const handleKeyboardSave = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        if (!isSubmitting) {
          handleSave(event as any);
        }
      }
    };

    document.addEventListener('keydown', handleKeyboardSave);
    return () => {
      document.removeEventListener('keydown', handleKeyboardSave);
    };
  }, [isSubmitting, formData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = parseInt(value, 10) || 0;
    
    // Validate metrics_retention range
    if (name === 'metrics_retention') {
      if (numValue < 1) {
        setMessage({ type: 'error', text: 'Metrics retention must be at least 1 minute' });
        return;
      }
      if (numValue > 10080) {
        setMessage({ type: 'error', text: 'Metrics retention cannot exceed 7 days (10,080 minutes)' });
        return;
      }
    }
    
    // Validate refresh_interval_seconds range
    if (name === 'refresh_interval_seconds') {
      if (numValue < 1) {
        setMessage({ type: 'error', text: 'Refresh interval must be at least 1 second' });
        return;
      }
      if (numValue > 300) {
        setMessage({ type: 'error', text: 'Refresh interval cannot exceed 300 seconds (5 minutes)' });
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: numValue
    }));
    
    // Clear any existing message
    setMessage(null);
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Parse numeric values for bucket_seconds
    const finalValue = name === 'bucket_seconds' ? parseInt(value, 10) : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
    
    // Clear any existing message
    setMessage(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate metrics retention
    if (formData.metrics_retention < 1 || formData.metrics_retention > 10080) {
      showToast('error', 'Metrics retention must be between 1 minute and 7 days (10,080 minutes)');
      return;
    }
    
    setIsSubmitting(true);
    setMessage(null);
    
    showToast('loading', 'Saving settings...');
    
    try {
      await actions.updateSettings(formData);
      showToast('success', 'Settings saved successfully!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save settings';
      showToast('error', errorMessage);
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = async () => {
    setIsSubmitting(true);
    setMessage(null);
    
    showToast('loading', 'Resetting settings to defaults...');
    
    try {
      await actions.resetSettings();
      showToast('success', 'Settings reset to defaults!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset settings';
      showToast('error', errorMessage);
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTruncateMetrics = async () => {
    setIsSubmitting(true);
    setMessage(null);
    
    showToast('loading', 'Truncating old metrics...');
    
    try {
      const result = await actions.truncateMetrics();
      const detailsMessage = `Truncated ${result.connections_removed} connections and ${result.events_removed} events. Retained ${result.connections_retained} connections and ${result.events_retained} events.`;
      showToast('success', `Old metrics truncated successfully! ${detailsMessage}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to truncate metrics';
      showToast('error', errorMessage);
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReloadChannels = async () => {
    setIsSubmitting(true);
    setMessage(null);
    
    showToast('loading', 'Reloading channel configuration...');
    
    try {
      await actions.reloadChannels();
      showToast('success', 'Channel configuration reloaded successfully!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reload channels';
      showToast('error', errorMessage);
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (state.isLoading.settings && !state.settings) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => navigate('/')}
          >
            <i className="fas fa-times"></i> Close
          </button>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
          <strong>{message.type === 'success' ? 'Success!' : 'Error!'}</strong> {message.text}
          <button type="button" className="close" onClick={() => setMessage(null)}>
            <span>&times;</span>
          </button>
        </div>
      )}

      {/* Dashboard Settings */}
      <div className="card shadow mb-3">
          <div className="card-header py-3">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-cogs"></i> Settings
            </h6>
          </div>
        <div className="card-body">
          <p className="text-muted mb-3">These settings only affect the dashboard UI and are stored locally in your browser.</p>
          <form onSubmit={handleSave}>
            <div className="row">
              <div className="col-md-6">
                <h6 className="mb-2 font-weight-bold">Identity Badge Settings</h6>
                <div className="form-group mb-3">
                  <label htmlFor="badge_threshold">Badge Time Threshold (minutes)</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    id="badge_threshold"
                    name="badge_threshold"
                    min="1"
                    max="1440"
                    value={formData.badge_threshold}
                    onChange={handleInputChange}
                    placeholder="Enter threshold in minutes"
                  />
                  <small className="form-text text-muted">
                    Identities created or used within this time will show "new" or "active" badges. Default: 5 minutes
                  </small>
                </div>

                <h6 className="mb-2 font-weight-bold">Metrics Retention</h6>
                <div className="form-group mb-3">
                  <label htmlFor="metrics_retention">Metrics Retention Period (minutes)</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    id="metrics_retention"
                    name="metrics_retention"
                    min="1"
                    max="10080"
                    value={formData.metrics_retention}
                    onChange={handleInputChange}
                    placeholder="Enter retention period in minutes"
                  />
                  <small className="form-text text-muted">
                    How long to keep connection and rule validation metrics. Default: 360 minutes (6 hours). Range: 1 minute to 10,080 minutes (7 days)
                  </small>
                </div>

                <h6 className="mb-2 font-weight-bold">Task Activity Window</h6>
                <div className="form-group mb-3">
                  <label htmlFor="task_activity_window">Task Activity Scope (seconds)</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    id="task_activity_window"
                    name="task_activity_window"
                    min="10"
                    max="600"
                    value={formData.task_activity_window}
                    onChange={handleInputChange}
                    placeholder="Enter activity window in seconds"
                  />
                  <small className="form-text text-muted">
                    Time window for calculating task throughput. Recent activity resets the window. Default: 60 seconds
                  </small>
                </div>

                <h6 className="mb-2 font-weight-bold">Onboarding Channel Settings</h6>
                <div className="form-group mb-3">
                  <label htmlFor="onboarding_channel_ttl_seconds">Temporary Channel Timeout (seconds)</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    id="onboarding_channel_ttl_seconds"
                    name="onboarding_channel_ttl_seconds"
                    min="10"
                    max="300"
                    value={formData.onboarding_channel_ttl_seconds}
                    onChange={handleInputChange}
                    placeholder="Enter timeout in seconds"
                  />
                  <small className="form-text text-muted">
                    How long temporary onboarding channels remain active before automatic deletion. Default: 30 seconds
                  </small>
                </div>

                <h6 className="mb-2 font-weight-bold">Dashboard Refresh Rate</h6>
                <div className="form-group mb-3">
                  <label htmlFor="refresh_interval_seconds">Auto-Refresh Interval (seconds)</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    id="refresh_interval_seconds"
                    name="refresh_interval_seconds"
                    min="1"
                    max="300"
                    value={formData.refresh_interval_seconds || 5}
                    onChange={handleInputChange}
                    placeholder="Enter refresh interval in seconds"
                  />
                  <small className="form-text text-muted">
                    Minimum time between dashboard data refreshes. Lower = more real-time but higher bandwidth. Default: 5 seconds. Range: 1-300 seconds (5 minutes)
                  </small>
                </div>

                <h6 className="mb-2 font-weight-bold">Log Display</h6>
                <div className="form-group mb-3">
                  <label htmlFor="log_timestamp_format">Log Timestamp Format</label>
                  <select
                    className="form-control form-control-sm dropdown-styling"
                    id="log_timestamp_format"
                    name="log_timestamp_format"
                    value={formData.log_timestamp_format || 'local'}
                    onChange={handleSelectChange}
                  >
                    <option value="local">Local Time (with timezone)</option>
                    <option value="utc">UTC (ISO 8601)</option>
                    <option value="relative">Relative (e.g., "2m ago")</option>
                    <option value="compact">Compact (time only)</option>
                  </select>
                  <small className="form-text text-muted">
                    Format for log entry timestamps in the Logs page. Default: Local Time
                  </small>
                </div>

                <h6 className="mb-2 font-weight-bold">Graph Settings</h6>
                <div className="form-group mb-3">
                  <label htmlFor="bucket_seconds">Time Series Bucket Interval</label>
                  <select
                    className="form-control form-control-sm dropdown-styling"
                    id="bucket_seconds"
                    name="bucket_seconds"
                    value={formData.bucket_seconds || 30}
                    onChange={handleSelectChange}
                  >
                    <option value={30}>30 seconds</option>
                    <option value={60}>1 minute</option>
                    <option value={300}>5 minutes</option>
                    <option value={900}>15 minutes</option>
                    <option value={1800}>30 minutes</option>
                    <option value={3600}>1 hour</option>
                    <option value={10800}>3 hours</option>
                    <option value={21600}>6 hours</option>
                  </select>
                  <small className="form-text text-muted">
                    Default bucket interval for aggregated connections time series graph. Can be overridden on the dashboard. Default: 30 seconds
                  </small>
                </div>

                <h6 className="mb-2 font-weight-bold">Sliding Window Scopes</h6>
                <div className="form-group mb-3">
                  <label htmlFor="connections_window">Total Connections Window (minutes)</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    id="connections_window"
                    name="connections_window"
                    min="1"
                    max="1440"
                    value={formData.connections_window}
                    onChange={handleInputChange}
                    placeholder="Enter connections window in minutes"
                  />
                  <small className="form-text text-muted">
                    Sliding window for total connections count. Default: 60 minutes
                  </small>
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="latency_window">Average Latency Window (minutes)</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    id="latency_window"
                    name="latency_window"
                    min="1"
                    max="1440"
                    value={formData.latency_window}
                    onChange={handleInputChange}
                    placeholder="Enter latency window in minutes"
                  />
                  <small className="form-text text-muted">
                    Sliding window for average latency calculations. Default: 60 minutes
                  </small>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={isSubmitting}
                >
                  <i className="fas fa-save"></i> {isSubmitting ? 'Saving...' : 'Save Settings'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm ml-2"
                  disabled={isSubmitting}
                  onClick={handleReset}
                >
                  <i className="fas fa-undo"></i> Reset to Defaults
                </button>
              </div>

              <div className="col-md-6">
                <h6 className="mb-2 font-weight-bold">About Badge Logic</h6>
                <div className="alert alert-info py-2">
                  <strong>Badge Behavior:</strong>
                  <ul className="mb-0">
                    <li><span className="badge badge-success">NEW</span> - Identity created within threshold</li>
                    <li><span className="badge badge-primary">ACTIVE</span> - Identity used within threshold</li>
                    <li>No badge - Identity hasn't been used recently</li>
                  </ul>
                </div>

                <h6 className="mb-2 font-weight-bold">About Task Activity Scope</h6>
                <div className="alert alert-info py-2">
                  <strong>Throughput Calculation:</strong>
                  <p className="mb-0">
                    The task activity window determines how recent activity affects throughput display.
                    If no activity occurs within this window, throughput shows 0 B/s.
                    Any new activity resets the window.
                  </p>
                </div>

                <h6 className="mb-2 font-weight-bold">About Metrics Retention</h6>
                <div className="alert alert-warning py-2">
                  <strong><i className="fas fa-exclamation-triangle"></i> Note:</strong>
                  <p className="mb-0">
                    Metrics older than the retention period are automatically deleted on restart.
                    Reducing this value will truncate historical data on the next restart.
                  </p>
                </div>

                <h6 className="mb-2 font-weight-bold">About Sliding Windows</h6>
                <div className="alert alert-info py-2">
                  <strong>Window Scopes:</strong>
                  <ul className="mb-0">
                    <li><strong>Connections Window:</strong> Tracks total connections in the specified time period</li>
                    <li><strong>Latency Window:</strong> Calculates average latency from connections in the specified time period</li>
                    <li>Shorter windows show more recent activity, longer windows provide broader trends</li>
                  </ul>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* System Settings */}
      <div className="card shadow mb-3">
        <div className="card-header py-2">
          <h6 className="m-0 font-weight-bold text-danger">
            <i className="fas fa-cogs"></i> System Settings
          </h6>
        </div>
        <div className="card-body">
          <p className="text-muted mb-3">These operations affect the backend system and apply changes immediately.</p>
          <div className="row">
            <div className="col-md-6">
              <h6 className="mb-2 font-weight-bold">Metrics Management</h6>
              <button
                type="button"
                className="btn btn-warning btn-sm"
                disabled={isSubmitting}
                onClick={handleTruncateMetrics}
              >
                <i className="fas fa-cut"></i> {isSubmitting ? 'Processing...' : 'Truncate Old Metrics Now'}
              </button>
              <small className="form-text text-muted d-block mt-1">
                Remove metrics older than the retention period immediately without restarting.
              </small>
            </div>
            <div className="col-md-6">
              <h6 className="mb-2 font-weight-bold">Channel Configuration</h6>
              <button
                type="button"
                className="btn btn-info btn-sm"
                disabled={isSubmitting}
                onClick={handleReloadChannels}
              >
                <i className="fas fa-sync-alt"></i> {isSubmitting ? 'Reloading...' : 'Reload Channel Configuration'}
              </button>
              <small className="form-text text-muted d-block mt-1">
                Reload channel configuration from DynamoDB and apply changes without restarting the proxy.
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;